import { useLiveQuery } from "dexie-react-hooks";
import { useMemo } from "react";
import type { ChatEntity } from "../db";
import { db } from "../db";

/**
 * Custom hook for searching chats by all message content
 * Searches through both user questions and assistant responses for comprehensive results
 */
export const useChatSearch = (chats: ChatEntity[] | undefined, search: string) => {
    // Get all messages for each chat to enable comprehensive search
    // Includes both user questions and assistant responses for maximum coverage
    const chatMessages = useLiveQuery(() => {
        if (!chats || chats.length === 0) return {} as Record<string, string>;
        return Promise.all(
            chats.map(async (chat) => {
                const allMessages = await db.messages
                    .where("chatId")
                    .equals(chat.id)
                    .limit(20) // Reasonable limit to balance performance vs coverage
                    .toArray();

                const combinedContent = allMessages
                    .filter((msg) => msg.content && msg.content.trim().length > 0)
                    .map((msg) => msg.content)
                    .join(" ")
                    .trim();

                return {
                    chatId: chat.id,
                    messageContent: combinedContent,
                };
            }),
        ).then((results) => {
            return results.reduce(
                (acc, { chatId, messageContent }) => {
                    acc[chatId] = messageContent;
                    return acc;
                },
                {} as Record<string, string>,
            );
        });
    }, [chats]);

    const filteredChats = useMemo(
        () =>
            (chats ?? []).filter((chat) => {
                if (search) {
                    const searchLower = search.toLowerCase();

                    // Search through all message content (user questions + assistant responses)
                    // This includes technical solutions, references, and any mentioned terms
                    const messageContent = chatMessages?.[chat.id] || "";
                    return messageContent.toLowerCase().includes(searchLower);
                }

                return true;
            }),
        [chats, search, chatMessages],
    );

    return filteredChats;
};
